import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { formatDuration } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Edit,
  Users,
  Clock,
  Calendar,
  Target,
  Plus,
  Trash2,
  Check,
  MoreHorizontal,
  ListTodo,
  Timer,
  UserMinus,
  AlertCircle,
  DollarSign,
  IndianRupee
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectDetail() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  // State
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [removeEmployeeModalOpen, setRemoveEmployeeModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Task form
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    assigned_to: '',
    estimated_hours: '',
    due_date: '',
    status: 'todo'
  });

  // Billing state
  const [billingRate, setBillingRate] = useState(0);
  const [billingRoles, setBillingRoles] = useState([]);
  const [memberRates, setMemberRates] = useState([]);
  const [billingSaving, setBillingSaving] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleRate, setNewRoleRate] = useState('');

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch data
  const fetchProject = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error('Project not found');
      }
      const data = await res.json();
      setProject(data);
    } catch (error) {
      toast.error(error.message);
      navigate('/admin/projects');
    }
  }, [token, projectId, navigate]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/employees/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProject(), fetchEmployees()]);
      setLoading(false);
    };
    if (token && projectId) loadData();
  }, [token, projectId, fetchProject, fetchEmployees]);

  // Sync billing state when project loads
  useEffect(() => {
    if (project) {
      setBillingRate(project.hourly_rate || 0);
      setBillingRoles(project.billing_roles || []);
      setMemberRates(project.member_rates || []);
    }
  }, [project]);

  // Handlers
  const openCreateTaskModal = () => {
    setTaskForm({
      name: '',
      description: '',
      assigned_to: '',
      estimated_hours: '',
      due_date: '',
      status: 'todo'
    });
    setSelectedTask(null);
    setTaskModalOpen(true);
  };

  const openEditTaskModal = (task) => {
    setTaskForm({
      name: task.name,
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      estimated_hours: task.estimated_hours || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      status: task.status || 'todo'
    });
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.name.trim()) {
      toast.error('Task name is required');
      return;
    }

    try {
      const payload = {
        ...taskForm,
        assigned_to: taskForm.assigned_to || null,
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
        due_date: taskForm.due_date || null
      };

      const url = selectedTask 
        ? `${API_URL}/api/admin/tasks/${selectedTask.id}`
        : `${API_URL}/api/admin/projects/${projectId}/tasks`;
      
      const res = await fetch(url, {
        method: selectedTask ? 'PUT' : 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('Failed to save task');
      }

      toast.success(selectedTask ? 'Task updated' : 'Task created');
      setTaskModalOpen(false);
      fetchProject();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      await fetch(`${API_URL}/api/admin/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      fetchProject();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      fetchProject();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const handleAddEmployee = async (employeeId) => {
    if (!employeeId || project.assigned_employees?.includes(employeeId)) return;

    try {
      await fetch(`${API_URL}/api/admin/projects/${projectId}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          assigned_employees: [...(project.assigned_employees || []), employeeId]
        })
      });
      toast.success('Employee added to project');
      fetchProject();
    } catch (error) {
      toast.error('Failed to add employee');
    }
  };

  const handleRemoveEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      await fetch(`${API_URL}/api/admin/projects/${projectId}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          assigned_employees: (project.assigned_employees || []).filter(id => id !== selectedEmployee.id)
        })
      });
      toast.success('Employee removed from project');
      setRemoveEmployeeModalOpen(false);
      fetchProject();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };


  // Billing handlers
  const handleSaveBilling = async () => {
    setBillingSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hourly_rate: parseFloat(billingRate) || 0, billing_roles: billingRoles, member_rates: memberRates })
      });
      if (res.ok) {
        toast.success('Billing rates saved');
        fetchProject();
      } else {
        toast.error('Failed to save billing rates');
      }
    } catch (e) { toast.error('Failed to save billing rates'); }
    setBillingSaving(false);
  };

  const addBillingRole = () => {
    if (!newRoleName.trim()) return;
    if (billingRoles.find(r => r.name.toLowerCase() === newRoleName.trim().toLowerCase())) {
      toast.error('Role already exists');
      return;
    }
    setBillingRoles(prev => [...prev, { name: newRoleName.trim(), default_rate: parseFloat(newRoleRate) || 0 }]);
    setNewRoleName('');
    setNewRoleRate('');
  };

  const updateRoleRate = (index, rate) => {
    setBillingRoles(prev => prev.map((r, i) => i === index ? { ...r, default_rate: parseFloat(rate) || 0 } : r));
  };

  const removeRole = (index) => {
    setBillingRoles(prev => prev.filter((_, i) => i !== index));
  };

  const addMemberRate = (userId) => {
    const emp = employees.find(e => e.id === userId);
    const assignedEmp = project?.assigned_employee_details?.find(e => e.id === userId);
    const name = assignedEmp?.name || emp?.name || 'Unknown';
    if (memberRates.find(mr => mr.user_id === userId)) return;
    setMemberRates(prev => [...prev, { user_id: userId, user_name: name, role: '', rate: billingRate || 0 }]);
  };

  const updateMemberRate = (userId, field, value) => {
    setMemberRates(prev => prev.map(mr => {
      if (mr.user_id !== userId) return mr;
      const updated = { ...mr, [field]: value };
      // If role changed, auto-fill rate from billing_roles default
      if (field === 'role') {
        const matchingRole = billingRoles.find(r => r.name === value);
        if (matchingRole) updated.rate = matchingRole.default_rate;
      }
      return updated;
    }));
  };

  const removeMemberRate = (userId) => {
    setMemberRates(prev => prev.filter(mr => mr.user_id !== userId));
  };

  // Helpers
  const getTaskStatusBadge = (status) => {
    switch (status) {
      case 'done': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'in_progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300';
    }
  };

  const getHealthIndicator = () => {
    if (!project?.estimated_hours) return null;
    const percentage = (project.total_hours / project.estimated_hours) * 100;
    if (percentage >= 100) return { color: 'bg-red-50 dark:bg-red-900/30', label: 'Over budget', text: 'text-red-600' };
    if (percentage >= 75) return { color: 'bg-amber-50 dark:bg-amber-900/30', label: 'At risk', text: 'text-amber-600' };
    return { color: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'On track', text: 'text-emerald-600' };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Project not found</p>
          <Button onClick={() => navigate('/admin/projects')} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </Layout>
    );
  }

  const health = getHealthIndicator();
  const progress = project.estimated_hours 
    ? Math.min((project.total_hours / project.estimated_hours) * 100, 100) 
    : 0;

  const unassignedEmployees = employees.filter(
    e => !project.assigned_employees?.includes(e.id)
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="project-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/projects')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: project.color }}
            />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{project.name}</h1>
            <Badge className={
              project.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
              project.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 dark:bg-slate-700 text-slate-700'
            }>
              {project.status === 'on_hold' ? 'On Hold' : project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
            </Badge>
          </div>
          <Button variant="outline" onClick={() => navigate(`/admin/projects?edit=${project.id}`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Project
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {project.total_hours?.toFixed(1) || '0'}h
                  </p>
                  <p className="text-sm text-slate-500">Hours Tracked</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {project.assigned_employee_details?.length || 0}
                  </p>
                  <p className="text-sm text-slate-500">Team Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        {project.description && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">{project.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="team" className="space-y-4">
          <TabsList>
            <TabsTrigger value="team">Team ({project.assigned_employee_details?.length || 0})</TabsTrigger>
            <TabsTrigger value="time-entries">Time Entries</TabsTrigger>
            <TabsTrigger value="billing" data-testid="billing-tab">Billing</TabsTrigger>
          </TabsList>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Assigned Employees</CardTitle>
                  {unassignedEmployees.length > 0 && (
                    <Select onValueChange={handleAddEmployee}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Add employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        {unassignedEmployees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {project.assigned_employee_details?.map((emp) => (
                    <div 
                      key={emp.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium overflow-hidden">
                          {emp.profile_photo ? (
                            <img src={emp.profile_photo} alt={emp.name} className="w-full h-full object-cover" />
                          ) : (
                            emp.name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{emp.name}</p>
                          <p className="text-sm text-slate-500">{emp.job_title || emp.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {emp.hours_tracked?.toFixed(1) || '0'}h
                          </p>
                          <p className="text-xs text-slate-500">tracked</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setRemoveEmployeeModalOpen(true);
                          }}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!project.assigned_employee_details || project.assigned_employee_details.length === 0) && (
                    <p className="text-center py-8 text-slate-500">No employees assigned</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Entries Tab */}
          <TabsContent value="time-entries">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Time Entries</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {project.time_entries?.map((entry) => (
                        <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-medium">
                                {entry.user?.name?.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium">{entry.user?.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            {entry.description || <span className="text-slate-400 italic">No description</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            {new Date(entry.start_time).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                            {formatDuration(entry.duration)}
                          </td>
                        </tr>
                      ))}
                      {(!project.time_entries || project.time_entries.length === 0) && (
                        <tr>
                          <td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                            No time entries yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" data-testid="billing-tab-content">
            <div className="space-y-6">
              {/* Section 1: Default Project Rate */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" /> Default Project Rate
                  </CardTitle>
                  <CardDescription>Fallback rate when no member-specific rate is configured.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 max-w-xs">
                    <span className="text-slate-500 text-sm">₹</span>
                    <Input
                      type="number"
                      value={billingRate}
                      onChange={e => setBillingRate(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      data-testid="project-hourly-rate-input"
                    />
                    <span className="text-slate-500 text-sm whitespace-nowrap">/ hour</span>
                  </div>
                </CardContent>
              </Card>

              {/* Section 2: Billing Roles */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Billing Roles</CardTitle>
                  <CardDescription>Define roles for this project with default hourly rates. Assign roles to team members below.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {billingRoles.length > 0 && (
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {billingRoles.map((role, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex-1">
                            <span className="text-sm font-medium text-slate-700">{role.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500 text-sm">₹</span>
                            <Input
                              type="number"
                              value={role.default_rate}
                              onChange={e => updateRoleRate(idx, e.target.value)}
                              className="w-28"
                              min="0"
                              step="0.01"
                            />
                            <span className="text-slate-500 text-xs whitespace-nowrap">/hr</span>
                            <button
                              onClick={() => removeRole(idx)}
                              className="p-1 hover:bg-red-50 dark:bg-red-900/30 rounded text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Role */}
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Role Name</Label>
                      <Input
                        value={newRoleName}
                        onChange={e => setNewRoleName(e.target.value)}
                        placeholder="e.g. Senior Developer"
                        data-testid="new-role-name-input"
                        onKeyDown={e => e.key === 'Enter' && addBillingRole()}
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-xs text-slate-500">Rate (₹/hr)</Label>
                      <Input
                        type="number"
                        value={newRoleRate}
                        onChange={e => setNewRoleRate(e.target.value)}
                        placeholder="0"
                        min="0"
                        data-testid="new-role-rate-input"
                        onKeyDown={e => e.key === 'Enter' && addBillingRole()}
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={addBillingRole} data-testid="add-role-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Role
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Section 3: Member Rates + Roles */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Team Member Rates</CardTitle>
                  <CardDescription>Assign roles and override rates per team member. Selecting a role auto-fills its default rate.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {memberRates.length > 0 && (
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {memberRates.map(mr => (
                        <div key={mr.user_id} className="flex items-center gap-3 px-4 py-3 flex-wrap sm:flex-nowrap">
                          <div className="w-36 shrink-0">
                            <span className="text-sm font-medium text-slate-700">{mr.user_name}</span>
                          </div>
                          <Select value={mr.role || '_none'} onValueChange={v => updateMemberRate(mr.user_id, 'role', v === '_none' ? '' : v)}>
                            <SelectTrigger className="w-44">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">No role</SelectItem>
                              {billingRoles.map((r, i) => (
                                <SelectItem key={i} value={r.name}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-500 text-sm">₹</span>
                            <Input
                              type="number"
                              value={mr.rate}
                              onChange={e => updateMemberRate(mr.user_id, 'rate', parseFloat(e.target.value) || 0)}
                              className="w-28"
                              min="0"
                              step="0.01"
                            />
                            <span className="text-slate-500 text-xs whitespace-nowrap">/hr</span>
                          </div>
                          <button
                            onClick={() => removeMemberRate(mr.user_id)}
                            className="p-1 hover:bg-red-50 dark:bg-red-900/30 rounded text-slate-400 hover:text-red-500 ml-auto"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Member */}
                  {project?.assigned_employee_details?.filter(e => !memberRates.find(mr => mr.user_id === e.id)).length > 0 && (
                    <Select onValueChange={addMemberRate}>
                      <SelectTrigger className="max-w-xs" data-testid="add-member-rate-select">
                        <SelectValue placeholder="Add team member..." />
                      </SelectTrigger>
                      <SelectContent>
                        {project.assigned_employee_details
                          .filter(e => !memberRates.find(mr => mr.user_id === e.id))
                          .map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>

              {/* Save */}
              <div className="flex justify-end">
                <Button onClick={handleSaveBilling} disabled={billingSaving} data-testid="save-billing-btn">
                  {billingSaving ? 'Saving...' : 'Save Billing Rates'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Task Modal */}
        <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
          <DialogContent data-testid="task-modal">
            <DialogHeader>
              <DialogTitle>{selectedTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
              <DialogDescription>
                {selectedTask ? 'Update task details' : 'Create a new task for this project'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Name *</Label>
                <Input
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  placeholder="e.g. Design homepage mockup"
                  data-testid="task-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task description (optional)"
                />
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select 
                  value={taskForm.assigned_to || 'unassigned'} 
                  onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to: v === 'unassigned' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {project.assigned_employee_details?.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Hours</Label>
                  <Input
                    type="number"
                    min="0"
                    value={taskForm.estimated_hours}
                    onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })}
                    placeholder="e.g. 4"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={taskForm.status} 
                  onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTask} data-testid="save-task-btn">
                {selectedTask ? 'Save Changes' : 'Add Task'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove Employee Confirmation */}
        <Dialog open={removeEmployeeModalOpen} onOpenChange={setRemoveEmployeeModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Employee</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove <strong>{selectedEmployee?.name}</strong> from this project?
                Their tracked time will be preserved.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveEmployeeModalOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRemoveEmployee}>
                <UserMinus className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
